import React, { createContext, useContext, useEffect, useState } from 'react';

export type Market = 'US' | 'IN';

interface MarketContextValue {
  market: Market;
  isLoading: boolean;
}

const MarketContext = createContext<MarketContextValue | undefined>(undefined);

let marketRequest: Promise<Market> | null = null;

const parseMarket = (value: string | null | undefined): Market | null => {
  const normalized = value?.trim().toUpperCase();
  return normalized === 'IN' || normalized === 'US' ? normalized : null;
};

const detectMarket = async (): Promise<Market> => {
  // Dev / QA override: http://localhost:3000/?market=IN or ?market=US
  const queryMarket = parseMarket(new URLSearchParams(window.location.search).get('market'));
  if (queryMarket) {
    return queryMarket;
  }

  // trim() tolerates shell quirks like cmd's `set VAR=IN && ...` adding a trailing space
  const configuredMarket = parseMarket(process.env.REACT_APP_MARKET);
  if (configuredMarket) {
    return configuredMarket;
  }

  try {
    const response = await fetch('/market.php', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) {
      return 'US';
    }

    const data = await response.json();
    return data.country === 'IN' ? 'IN' : 'US';
  } catch {
    return 'US';
  }
};

export const MarketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [market, setMarket] = useState<Market>('US');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!marketRequest) {
      marketRequest = detectMarket();
    }

    marketRequest.then((detectedMarket) => {
      setMarket(detectedMarket);
      setIsLoading(false);
    });
  }, []);

  return (
    <MarketContext.Provider value={{ market, isLoading }}>
      {children}
    </MarketContext.Provider>
  );
};

export const useMarket = (): MarketContextValue => {
  const context = useContext(MarketContext);

  if (!context) {
    throw new Error('useMarket must be used within a MarketProvider');
  }

  return context;
};
